// Persists the user's preference to be notified when an Insights segment
// query finishes. The user can arm multiple segments at the same time —
// each arm fires exactly once (single-shot) when its own segment
// completes. Only saved segments can be armed — we don't notify for
// segmentless / ad-hoc "all clients" runs.
//
// Storage shape: { firmId: number, segmentIds: number[] }. The list is
// per-firm so switching firms starts fresh.

type Id = number | null

interface Stored {
  firmId: number
  segmentIds: Id[]
}

const STORAGE_KEY = 'insights:segmentReady'
// Tracks which segment the user is currently viewing on the Insights
// companies overview page. Set by companies-list-status.vue on mount
// and on every segment change. Used by `consumeSegmentReadyArm` to
// suppress redundant toasts and by the toast click handler to decide
// between an in-place segment switch and full-page navigation.
let activeInsightsSegmentId: number | null = null

/**
 * True when the user is currently on the Insights companies overview
 * page. Used by `consumeSegmentReadyArm` to suppress redundant toasts
 * and by the toast click handler to switch segments in-place instead
 * of triggering a full-page navigation.
 */
export function isOnInsights(): boolean {
  const firmId = window.Silverfin?.currentFirmId
  if (!firmId) return false

  let expected: string
  try {
    expected = window.Silverfin.Routes.companies_overview_index_path({
      firm_id: firmId,
    })
  } catch {
    return false
  }
  return window.location.pathname === expected
}

// Normalize any id coming from payloads, props, or storage into a canonical
// number | null. Strings are tolerated because ids sometimes arrive as
// JSON-stringified numbers.
function toId(value: number | string | null | undefined): Id {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function read(): Stored | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<Stored>
    if (typeof parsed?.firmId !== 'number') return null

    if (!Array.isArray(parsed.segmentIds)) return null

    return {
      firmId: parsed.firmId,
      segmentIds: parsed.segmentIds.map(toId),
    }
  } catch {
    return null
  }
}

function write(stored: Stored | null): void {
  try {
    // Treat any empty/degenerate state as "no arm" — writing an empty
    // shape would leave a stale `{}` (or similar) in localStorage that
    // the spec would observe even though the user is no longer armed.
    if (
      stored === null ||
      stored.segmentIds.length === 0 ||
      typeof stored.firmId !== 'number'
    ) {
      window.localStorage.removeItem(STORAGE_KEY)
    } else {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
    }
  } catch {
    // ignore quota / privacy mode errors
  }
}

/**
 * Returns true if the user has armed an alert for the given firm. If
 * `segmentId` is provided, also requires that segment to be in the
 * armed set.
 */
export function isSegmentReadyArmed(
  firmId: number | string | undefined,
  segmentId?: number | string | null
): boolean {
  const firm = toId(firmId)
  if (firm === null) {
    return false
  }

  const stored = read()
  if (!stored || stored.firmId !== firm) {
    return false
  }

  if (segmentId === undefined) {
    return stored.segmentIds.length > 0
  }

  return stored.segmentIds.includes(toId(segmentId))
}

/**
 * Adds a segment to the armed set for the given firm. If the stored firm
 * differs from the one being armed, the old set is discarded — a user
 * can't have arms across multiple firms simultaneously from the same
 * browser.
 */
export function armSegmentReady(
  firmId: number | string,
  segmentId: number | string | null | undefined
): void {
  const firm = toId(firmId)
  if (firm === null) {
    return
  }

  const target = toId(segmentId)
  const stored = read()
  const existing = stored && stored.firmId === firm ? stored.segmentIds : []
  if (existing.includes(target)) {
    return
  }
  write({ firmId: firm, segmentIds: [...existing, target] })
}

/**
 * Removes a segment from the armed set. If the set becomes empty, clears
 * the stored entry entirely. Call with `segmentId: undefined` to clear
 * every armed segment for every firm.
 */
export function disarmSegmentReady(segmentId?: number | string | null): void {
  if (segmentId === undefined) {
    write(null)
    return
  }

  const stored = read()
  if (!stored) {
    return
  }

  const target = toId(segmentId)
  const remaining = stored.segmentIds.filter((id) => id !== target)
  write(
    remaining.length === 0
      ? null
      : { firmId: stored.firmId, segmentIds: remaining }
  )
}

/**
 * Decides whether an incoming global toast should be raised. Toasts that
 * carry `insights_segment_ready_segment_id` are gated on the user having
 * armed an alert for that segment in this firm — without an arm, the
 * toast is dropped. When an arm matches, it is consumed (single-shot).
 * If the user is already viewing the matching segment on Insights the
 * arm is consumed but the toast is suppressed — they're already there.
 * Toasts without the tag pass through unchanged.
 */
export function consumeSegmentReadyArm(
  firmId: number | string | undefined,
  payload: Record<string, unknown>
): boolean {
  const segmentId = payload.insights_segment_ready_segment_id as string

  if (!payload.insights_segment_ready_segment_id) {
    return true
  }

  if (!isSegmentReadyArmed(firmId, segmentId)) {
    return false
  }

  disarmSegmentReady(segmentId)

  const normalizedId = toId(segmentId)

  if (
    normalizedId !== null &&
    isOnInsights() &&
    activeInsightsSegmentId === normalizedId
  ) {
    return false
  }

  return true
}

/**
 * Records the segment the user is currently viewing on the Insights
 * companies overview. Pass `null` for the all-clients (segmentless) view.
 */
export function setActiveInsightsSegmentId(segmentId: number | null): void {
  activeInsightsSegmentId = segmentId
}
